/** Side-by-side artefact preview and Ace vs human comparison from calibration. */

import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowUpRightFromSquare,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { CompanyProvider } from "@/context/CompanyContext";
import { Dialog, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import {
  agentReviewAPI,
  type CalibrationResolutionOutcome,
  type CheckDisagreementDetail,
} from "@/services/api/agentReview";
import { AceArtefactComparison } from "./AceArtefactComparison";
import { CalibrationResolutionControls } from "./CalibrationResolutionControls";
import { LoaArtefactPreview } from "./LoaArtefactPreview";
import { caseDisplayLabel } from "./calibrationAnalysisHelpers";
import { viewSubmittedCaseIntent, type ReviewCaseOpenIntent } from "./reviewCaseOpenIntent";
import type { AgentRun, ArtefactReviewItem, CheckAnswers, CheckComments, PhaseCriteria, Rag } from "./types";

interface ArtefactCalibrationDrawerProps {
  open: boolean;
  dispute: CheckDisagreementDetail | null;
  saving?: boolean;
  onClose: () => void;
  onResolve: (
    item: CheckDisagreementDetail,
    outcome: CalibrationResolutionOutcome,
    note: string,
  ) => void;
  onClear: (item: CheckDisagreementDetail) => void;
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
}

function humanArtefactAnswers(
  comparison: Awaited<ReturnType<typeof agentReviewAPI.getReviewComparison>>,
  artifactId: string,
): CheckAnswers {
  const rating = comparison.human_artefact_ratings?.find((item) => item.artifact_id === artifactId);
  if (!rating) return {};
  return Object.fromEntries(rating.checks.map((check) => [check.id, check.answer]));
}

function humanArtefactComments(
  comparison: Awaited<ReturnType<typeof agentReviewAPI.getReviewComparison>>,
  artifactId: string,
): CheckComments {
  const rating = comparison.human_artefact_ratings?.find((item) => item.artifact_id === artifactId);
  if (!rating) return {};
  return Object.fromEntries(
    rating.checks
      .filter((check) => check.comment?.trim())
      .map((check) => [check.id, check.comment ?? ""]),
  );
}

function humanArtefactRag(
  comparison: Awaited<ReturnType<typeof agentReviewAPI.getReviewComparison>>,
  artifactId: string,
): Rag | null {
  return comparison.human_artefact_ratings?.find((item) => item.artifact_id === artifactId)?.human_rag ?? null;
}

export function ArtefactCalibrationDrawer({
  open,
  dispute,
  saving = false,
  onClose,
  onResolve,
  onClear,
  onOpenCase,
}: ArtefactCalibrationDrawerProps) {
  const [comparison, setComparison] = useState<Awaited<
    ReturnType<typeof agentReviewAPI.getReviewComparison>
  > | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !dispute?.review_id) {
      setComparison(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void agentReviewAPI
      .getReviewComparison(dispute.review_id)
      .then((result) => {
        if (!cancelled) setComparison(result);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load artefact comparison.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, dispute?.review_id]);

  const artefact = useMemo<ArtefactReviewItem | null>(() => {
    if (!dispute?.artifact_id || !comparison) return null;
    return comparison.artefacts?.find((item) => item.artifact_id === dispute.artifact_id) ?? null;
  }, [comparison, dispute?.artifact_id]);

  const criteria = comparison?.artefact_review_criteria as PhaseCriteria | null | undefined;

  const runStub = useMemo<AgentRun | null>(() => {
    if (!comparison) return null;
    return {
      run_id: comparison.run_id,
      source: comparison.source,
      task_name: comparison.case_context?.task_name ?? "Case",
      task_id: comparison.case_context?.task_id ?? null,
      interaction_id: comparison.case_context?.interaction_id ?? comparison.run_id,
      available_tools: [],
      available_actions: [],
      available_embeds: [],
      steps: [],
    };
  }, [comparison]);

  if (!dispute) return null;

  const title = dispute.artifact_title?.trim() || caseDisplayLabel(dispute);

  return (
    <Dialog open={open} onClose={onClose} size="5xl" className="!max-w-6xl">
      <DialogTitle className="flex flex-wrap items-start justify-between gap-3 pr-8">
        <div className="min-w-0 space-y-1">
          <p className="text-base font-medium text-slate-100">{title}</p>
          <p className="text-xs text-slate-500">
            {dispute.subject_label || "Case"}
            <span className="text-slate-600"> · </span>
            {dispute.check_label}
          </p>
        </div>
        <Button
          plain
          className="!text-indigo-300 hover:!text-indigo-200"
          onClick={() =>
            onOpenCase(
              dispute.run_id,
              dispute.subject_label || undefined,
              viewSubmittedCaseIntent(dispute),
            )
          }
        >
          Open full case
          <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
        </Button>
      </DialogTitle>

      <DialogBody className="space-y-4">
        {error ? <p className="text-sm text-rose-400">{error}</p> : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:min-h-[28rem]">
          <div className="flex min-h-[18rem] flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Document</p>
            {artefact ? (
              <CompanyProvider>
                <LoaArtefactPreview artefact={artefact} className="min-h-[18rem] flex-1" />
              </CompanyProvider>
            ) : loading ? (
              <div className="flex flex-1 items-center justify-center rounded-xl bg-white/[0.02] ring-1 ring-white/5 text-sm text-slate-500">
                Loading preview…
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl bg-white/[0.02] px-4 py-8 text-sm text-slate-500 ring-1 ring-white/5">
                Preview unavailable for this artefact.
              </div>
            )}
            {artefact?.preview_path ? (
              <a
                href={artefact.preview_path}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200"
              >
                Full preview
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
              </a>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-col gap-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Human vs Ace
            </p>
            {criteria && artefact && runStub ? (
              <AceArtefactComparison
                run={runStub}
                artefact={artefact}
                criteria={criteria}
                humanAnswers={comparison ? humanArtefactAnswers(comparison, artefact.artifact_id) : {}}
                humanCheckComments={
                  comparison ? humanArtefactComments(comparison, artefact.artifact_id) : {}
                }
                humanRag={comparison ? humanArtefactRag(comparison, artefact.artifact_id) : null}
                comparison={comparison}
                loading={loading}
                focusCheckId={dispute.check_id}
                readOnly
              />
            ) : (
              <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-500 ring-1 ring-white/5">
                {loading ? "Loading comparison…" : "Artefact comparison unavailable."}
              </div>
            )}

            <div className="rounded-xl bg-white/[0.03] px-3 py-3 ring-1 ring-white/5">
              <CalibrationResolutionControls
                item={dispute}
                saving={saving}
                onResolve={(outcome, note) => onResolve(dispute, outcome, note)}
                onClear={() => onClear(dispute)}
              />
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
